import { useNode } from "@craftjs/core";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { OEmbedError } from "src/types";

import { Input } from "@/components/shared/forms";
import { Spinner } from "@/components/shared/spinner";
import { logProductAnalyticsEvent } from "@/features/analytics";
import logger from "@/utils/logger";
import {
  fetchVideoMetadata,
  OEmbedErrorTranslationKeys,
} from "@/utils/o-embed";
import isValidUrlProtocol from "@/utils/validate-url-protocol";

import type { VideoBlockProps } from "../video";
import { AdvancedSettings } from "./advanced-settings";

export const VideoURLSetting = () => {
  const {
    props,
    actions: { setProp },
  } = useNode((query) => ({
    props: query.props as VideoBlockProps,
  }));

  const [desiredUrl, setDesiredUrl] = useState<string | null>(props.url);
  const [hasError, setHasError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isEmbedding, setIsEmbedding] = useState<boolean>(false);
  const { t } = useTranslation();

  const handleTextChange = useCallback((event) => {
    setDesiredUrl(event.target.value);
  }, []);

  const handleEmbedPress = useCallback(async () => {
    const trimmedUrl = desiredUrl?.trim();
    if (!trimmedUrl) {
      return;
    }
    if (!isValidUrlProtocol(trimmedUrl)) {
      setHasError(true);
      setErrorMessage(t("preference-panel.invalid-url-error"));
      return;
    }

    const newVideoBlockProps: VideoBlockProps = {
      url: new URL(trimmedUrl).toString(),
    };

    try {
      setIsEmbedding(true);
      const oEmbedData = await fetchVideoMetadata(newVideoBlockProps.url);

      if ("error" in oEmbedData) {
        setHasError(true);
        setErrorMessage(
          t(`preference-panel.${OEmbedErrorTranslationKeys[oEmbedData.error]}`)
        );
      } else {
        const { title, thumbnail } = oEmbedData;
        newVideoBlockProps.title = title;
        newVideoBlockProps.thumbnail = thumbnail;

        setProp((props: VideoBlockProps) => {
          setHasError(false);
          props.recordingId = null;
          props.url = newVideoBlockProps.url;
          props.title = newVideoBlockProps.title;
          props.thumbnail = newVideoBlockProps.thumbnail;
          props.displayTitle = undefined;
          props.displayDescription = undefined;
          props.displayThumbnail = undefined;
        });

        logProductAnalyticsEvent({
          name: "ch_canvas_block_edit_video_url",
          properties: { url: newVideoBlockProps.url },
        });
      }
    } catch (e) {
      logger.error(new OEmbedError("Error during embedding a video"), {
        details: JSON.stringify(e),
      });
      setHasError(true);
      setErrorMessage(t("preference-panel.error-fetching-video-data"));
    } finally {
      setIsEmbedding(false);
    }
  }, [desiredUrl, setProp, t]);

  useEffect(
    /**
     * this component uses local state to validate URLs before actually setting them to props.url,
     * when user chooses a Recording we need to clear local state so that
     * the TextField doesn't show the previous video URL
     */
    function resetEmbedURL() {
      if (desiredUrl && props.url == null) {
        setDesiredUrl(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.recordingId]
  );

  return (
    <div className="mt-4 flex flex-col gap-y-4 p-4">
      <Input
        placeholder={t("preference-panel.video-url")}
        label={t("preference-panel.video-url")}
        required
        error={hasError && errorMessage}
        onChange={handleTextChange}
        value={desiredUrl ?? ""}
      />
      <p className="text-xs text-gray-600">
        {t("preference-panel.add-a-video")}
      </p>
      <p className="text-xs text-gray-600">
        {t("preference-panel.supported-platforms")}
      </p>
      <button
        className="flex h-8 flex-row items-center justify-center gap-2 rounded border border-transparent bg-blue-500 px-3 py-1 text-sm font-medium leading-5 text-white hover:bg-blue-600"
        onClick={handleEmbedPress}
        disabled={isEmbedding}
      >
        {isEmbedding ? <Spinner className="text-white" size="tiny" /> : null}
        {t("preference-panel.embed-video")}
      </button>
      <AdvancedSettings />
    </div>
  );
};
